// src/components/Billing/index.jsx
import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { Box } from "@mui/system";
import {
  Paper,
  Typography,
  Tooltip,
  Button,
  Chip,
  Stack,
  Divider,
  IconButton,
} from "@mui/material";
import Collapse from "@mui/material/Collapse";
import LoadingComponent from "../../components/ComponentLoader";
import BillingList, { normalizeBillPartySeed } from "./BillingList";
import {
  buildBillGroupKey,
  resolveBillingPoGroupContext,
} from "./billingTableRows";
import BillingModal from "./BillingModal";
import { savedBillPreTaxSubtotal } from "./BillingModal/MainBillingForm";
import { useParams, useHistory } from "react-router-dom";
import { fetchBills, updateBill } from "../../services/billing";
import { fetchAllPurchaseOrders } from "../../services/purchaseOrder";
import { normalizePurchaseOrderCharges } from "../../utils/purchaseOrderCharges";
import SettingsIcon from "@mui/icons-material/Settings";
import ButtonLoader from "../../components/ButtonLoader";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { fetchBillingFinancialSummary, fetchInvoicesSummary } from "../../services/invoices";
import { fetchLatestCompletedSalesOrderForOrder } from "../../services/estimation.js";
import "./style.css";
import {
  createBlankBillingModalContext,
  createEditBillingModalContext,
  createPoBillingModalContext,
} from "./billingModalContext";

const currency = (n) => `CA$${(Number(n) || 0).toFixed(2)}`;
const toNum = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};
export const getBillSubtotal = (bill = {}) => savedBillPreTaxSubtotal(bill);

export const derivePoAmount = (po = {}) => {
  const supplierInfo = po?.supplierInfo && typeof po.supplierInfo === "object"
    ? po.supplierInfo
    : {};

  const charges = normalizePurchaseOrderCharges(
    Array.isArray(supplierInfo?.charges) ? supplierInfo.charges : []
  );
  const chargesSubtotal = charges.reduce((sum, ch) => {
    const qty = toNum(ch?.Quantity ?? ch?.quantity ?? 1, 1);
    const unit = toNum(
      ch?.NetCost ??
      ch?.netCost ??
      ch?.UnitCost ??
      ch?.unitCost ??
      ch?.supplierCost ??
      ch?.SupplierCost,
      NaN
    );

    if (Number.isFinite(unit) && unit > 0) return sum + (qty * unit);
    return sum + toNum(ch?.Total ?? ch?.total, 0);
  }, 0);

  const poItems = Array.isArray(po?.items) ? po.items : [];
  const itemSubtotal = poItems.reduce((sum, item) => {
    const itemPricings = Array.isArray(item?.pricings) ? item.pricings : [];

    // Prefer PO cost math: NetCost * Quantity (not retail/client totals).
    const pricingNetTotal = itemPricings.reduce((acc, pricingRow) => {
      const qty = toNum(
        pricingRow?.Quantity ?? pricingRow?.quantity ?? item?.Quantity ?? item?.quantity ?? item?.qty,
        0
      );
      const unitNet = toNum(
        pricingRow?.NetCost ??
        pricingRow?.netCost ??
        pricingRow?.UnitCost ??
        pricingRow?.unitCost ??
        pricingRow?.Cost ??
        pricingRow?.cost,
        NaN
      );
      if (!Number.isFinite(unitNet)) return acc;
      return acc + (qty * unitNet);
    }, 0);

    if (pricingNetTotal > 0) return sum + pricingNetTotal;

    const qty = toNum(item?.Quantity ?? item?.quantity ?? item?.qty, 0);
    const unit = toNum(
      item?.supplierCost ??
      item?.SupplierCost ??
      item?.netCost ??
      item?.NetCost ??
      item?.UnitCost ??
      item?.unitCost ??
      item?.Cost ??
      item?.cost,
      0
    );

    return sum + (qty * unit);
  }, 0);

  const poType = String(supplierInfo?.type || po?.purchaseOrderType || "").toLowerCase();

  // PO progress compares pre-tax procurement cost targets against pre-tax bill subtotals.
  if (poType === "different_supplier_decoration") {
    const base = chargesSubtotal > 0 ? chargesSubtotal : itemSubtotal;
    return Number(base.toFixed(2));
  }

  if (poType === "no_decoration") {
    return Number(itemSubtotal.toFixed(2));
  }

  if (poType.startsWith("other_service_")) {
    return Number((itemSubtotal + chargesSubtotal).toFixed(2));
  }

  if (itemSubtotal > 0 || chargesSubtotal > 0) {
    return itemSubtotal + chargesSubtotal;
  }

  return toNum(po?.poAmount, 0);
};

export const buildEditBillPoContext = ({ billId, groupedBills = {}, purchaseOrders = [] } = {}) => {
  if (billId == null || billId === "") return null;

  const persistedBill = Object.values(groupedBills)
    .flatMap((bills) => (Array.isArray(bills) ? bills : []))
    .find((bill) => String(bill?.id) === String(billId));
  const persistedPoNumber = String(persistedBill?.poNumber || "").trim();
  if (!persistedPoNumber) return null;

  const purchaseOrder = purchaseOrders.find(
    (po) => String(po?.poNumber || "").trim() === persistedPoNumber
  );
  if (!purchaseOrder) return null;

  const supplierInfo =
    purchaseOrder?.supplierInfo && typeof purchaseOrder.supplierInfo === "object"
      ? purchaseOrder.supplierInfo
      : {};
  const shippingAccountId =
    supplierInfo?.shippingAccountId ?? supplierInfo?.shippingAccount?.id ?? null;
  const shippingAccountName = String(
    supplierInfo?.shippingAccount?.accountName || supplierInfo?.shippingAccount?.name || ""
  ).trim();
  const isOtherService = supplierInfo?.party?.partyType === "other-service";

  return createPoBillingModalContext({
    poNumber: persistedPoNumber,
    poStatus: purchaseOrder?.supplierStatus || persistedBill?.poStatus,
    poAmount: derivePoAmount(purchaseOrder),
    poSeedData: normalizeBillPartySeed({
      poId: purchaseOrder?.id,
      poNumber: persistedPoNumber,
      poType: supplierInfo?.type || purchaseOrder?.purchaseOrderType || "",
      supplierId:
        supplierInfo?.supplierId ??
        supplierInfo?.vendorSupplierId ??
        purchaseOrder?.supplierId ??
        null,
      decoratorId: supplierInfo?.decoratorId ?? purchaseOrder?.decoratorId ?? null,
      shippingAccountId,
      shippingAccount: shippingAccountId
        ? { id: shippingAccountId, accountName: shippingAccountName }
        : null,
      billPartyType: isOtherService ? "other-service" : undefined,
      billPartySourceType: isOtherService ? "other-service" : undefined,
      supplierName: supplierInfo?.supplierName || purchaseOrder?.supplierName || "",
      decoratorName: supplierInfo?.decoratorName || supplierInfo?.decoratorTitle || "",
      supplierInfo,
      poPartyName:
        supplierInfo?.party?.partyName ||
        shippingAccountName ||
        supplierInfo?.decoratorName ||
        supplierInfo?.decoratorTitle ||
        supplierInfo?.supplierName ||
        purchaseOrder?.supplierName ||
        "",
      items: Array.isArray(purchaseOrder?.items) ? purchaseOrder.items : [],
      _poItems: Array.isArray(purchaseOrder?.items) ? purchaseOrder.items : [],
    }),
  });
};

const fmtWhen = (iso) => {
  try {
    if (!iso) return "";
    const d = new Date(iso);
    return d.toLocaleString();
  } catch {
    return String(iso || "");
  }
};

// Small helper to place a discreet info icon with a tooltip
const InfoDot = ({ title, sx }) => (
  <Tooltip title={title} arrow>
    <IconButton size="small" sx={{ p: 0.25, ml: 0.5, ...sx }}>
      <InfoOutlinedIcon fontSize="inherit" />
    </IconButton>
  </Tooltip>
);

// Tiny clickable icon to toggle the breakdown panel
const ToggleBreakdownDot = ({ open, onToggle, sx }) => (
  <Tooltip title={open ? "Hide finance breakdown" : "Show finance breakdown"} arrow>
    <IconButton size="small" onClick={onToggle} sx={{ p: 0.25, ml: 0.25, ...sx }}>
      {open ? <InfoOutlinedIcon fontSize="inherit" /> : <HelpOutlineIcon fontSize="inherit" />}
    </IconButton>
  </Tooltip>
);

const Billing = ({ bookedMargin = 0, onComputed }) => {
  const [loading, setLoading] = useState(false);
  const { instanceUniqueId, orderId, tabId } = useParams();
  const history = useHistory();

  const [billingModalopen, setBillingModelOpen] = useState(false);
  const [billingModalContext, setBillingModalContext] = useState(createBlankBillingModalContext);
  const { billId, poNumber, poStatus, poSeedData, poAmount } = billingModalContext;

  const [groupedBills, setGroupedBills] = useState({});
  const [fullBillAmount, setFullBillAmount] = useState(0); // Booked (Target) from PO targets
  const [poAllBillsReceivedStatus, setPoAllBillsReceivedStatus] = useState({});
  const [hasGeneratedPurchaseOrders, setHasGeneratedPurchaseOrders] = useState(false);
  const [orderPurchaseOrders, setOrderPurchaseOrders] = useState([]);
  const [billingGateLoaded, setBillingGateLoaded] = useState(false);

  // 🔎 SO stamp + invoice summary
  const [stampedBookedAmount, setStampedBookedAmount] = useState(null);
  const [financialSummary, setFinancialSummary] = useState({
    totalBills: 0,
    billedMarginAmount: 0,
    billedMarginPercentage: null,
    salesSubtotal: 0,
    currency: null,
  });
  const [invoiceSummary, setInvoiceSummary] = useState({
    totalInvoiced: 0,
    paidInvoiced: 0,
    unpaidInvoiced: 0,
    count: 0,
    currency: "CAD",
  });

  const [showBillActions, setShowBillActions] = useState(false);
  const toggleBillActions = () => setShowBillActions((s) => !s);

  // NEW: breakdown toggle (SO Baseline + Invoices + Commission Gate)
  const [showBreakdown, setShowBreakdown] = useState(false);

  const resetBillingModalContext = useCallback(() => {
    setBillingModalContext(createBlankBillingModalContext());
  }, []);

  const openNewBill = () => {
    resetBillingModalContext();
    setBillingModelOpen(true);
  };

  const openBillFromPo = useCallback((poContext = {}) => {
    setBillingModalContext(createPoBillingModalContext({
      ...poContext,
      poSeedData: normalizeBillPartySeed(poContext?.poSeedData),
    }));
    setBillingModelOpen(true);
  }, []);

  const onBillClick = (id) => {
    setBillingModalContext(createEditBillingModalContext(id));
    history.push(
      `/instance/${instanceUniqueId}/sales/orders/${orderId}/bills/${id}`
    );
    setBillingModelOpen(true);
  };

  const getPurchaseOrderHref = useCallback((billRow) => {
    const poId = billRow?._poId;
    if (!poId) return "#";
    return `/generated-purchase-order/${poId}`;
  }, []);

  const closeBillingModal = () => {
    history.push(`/instance/${instanceUniqueId}/sales/orders/${orderId}/bills`);
    setBillingModelOpen(false);
    resetBillingModalContext();
  };

  useEffect(() => {
    if (!tabId) return;
    setBillingModalContext(createEditBillingModalContext(tabId));
    setBillingModelOpen(true);
  }, [tabId]);

  useEffect(() => {
    if (!billId || poNumber) return;
    const hydratedContext = buildEditBillPoContext({
      billId,
      groupedBills,
      purchaseOrders: orderPurchaseOrders,
    });
    if (!hydratedContext) return;
    setBillingModalContext({ ...hydratedContext, billId });
  }, [billId, groupedBills, orderPurchaseOrders, poNumber]);

  // --------------------------
  // Fetch Bills
  // --------------------------
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      setBillingGateLoaded(false);
      setOrderPurchaseOrders([]);
      const response = await fetchBills(orderId);
      const bills = response?.data?.billingData || [];
      try {
        const summary = await fetchBillingFinancialSummary(orderId);
        setFinancialSummary({
          totalBills: Number(summary?.totalBills) || 0,
          billedMarginAmount: Number(summary?.billedMarginAmount) || 0,
          billedMarginPercentage: summary?.billedMarginPercentage != null
            && Number.isFinite(Number(summary.billedMarginPercentage))
            ? Number(summary.billedMarginPercentage)
            : null,
          salesSubtotal: Number(summary?.salesSubtotal) || 0,
          currency: summary?.currency || null,
        });
      } catch (summaryError) {
        setFinancialSummary({ totalBills: 0, billedMarginAmount: 0, billedMarginPercentage: null, salesSubtotal: 0, currency: null });
        console.warn("[Billing] Unable to load financial summary:", summaryError?.message || summaryError);
      }
      let hasActiveGeneratedPurchaseOrders = false;

      // Group PO-backed bills by PO number, and standalone bills by bill identity.
      const grouped = bills.reduce((acc, curr, index) => {
        const po = buildBillGroupKey(curr, index);
        if (!acc[po]) acc[po] = [];
        acc[po].push(curr);
        return acc;
      }, {});

      // Sort each PO group by createdAt
      Object.keys(grouped).forEach((po) => {
        grouped[po].sort(
          (a, b) => new Date(a.createdAt) - new Date(b.createdAt)
        );
      });

      // Also surface PO rows that have no bill yet as "awaiting bill" placeholders.
      // This keeps the table populated with project POs even when bills are empty.
      try {
        const so = await fetchLatestCompletedSalesOrderForOrder(orderId);
        const salesOrderId = Number(so?.estimationId || so?.id || so?.salesOrderId);
        if (Number.isFinite(salesOrderId)) {
          const poRes = await fetchAllPurchaseOrders(salesOrderId);
          const poList = Array.isArray(poRes?.purchaseOrders) ? poRes.purchaseOrders : [];
          const activePoList = poList.filter((po) => po?.isDeleted !== true);
          setOrderPurchaseOrders(activePoList);
          hasActiveGeneratedPurchaseOrders = activePoList.length > 0;

          activePoList.forEach((po) => {
            const poNumber = po?.poNumber || `PO-${po?.id}`;
            const supplierName =
              po?.supplierInfo?.supplierName ||
              po?.supplierName ||
              "—";

            const poItems = Array.isArray(po?.items) ? po.items : [];
            const poSupplierInfo = po?.supplierInfo && typeof po?.supplierInfo === "object"
              ? po.supplierInfo
              : {};
            const poShippingAccountName =
              poSupplierInfo?.shippingAccount?.accountName ||
              poSupplierInfo?.shippingAccount?.name ||
              "";
            const poDecoratorName =
              poSupplierInfo?.decoratorName ||
              poSupplierInfo?.decoratorTitle ||
              "";
            const poType = String(poSupplierInfo?.type || po?.purchaseOrderType || "");
            const isOtherServicePo = poSupplierInfo?.party?.partyType === "other-service" &&
              poSupplierInfo?.party?.sourceType && poSupplierInfo?.party?.sourceId;
            const resolvedPoAmount = derivePoAmount(po);

            // If bills already exist for this PO, normalize their display target from PO data.
            if (Array.isArray(grouped[poNumber]) && grouped[poNumber].length) {
              grouped[poNumber] = grouped[poNumber].map((billRow) => ({
                ...billRow,
                _poSource: "purchase-order",
                poAmount: resolvedPoAmount,
                _poTargetAmount: resolvedPoAmount,
                _poNumber: poNumber,
                _poStatus: po?.supplierStatus || "Created",
                poStatus: po?.supplierStatus || billRow?.poStatus || "Created",
                supplierInfo: supplierName || billRow?.supplierInfo,
                _poPartyName:
                  isOtherServicePo
                    ? (poSupplierInfo.party.partyName || supplierName)
                    : billRow?.billPartyType === "shipping-account"
                    ? (poShippingAccountName || supplierName)
                    : billRow?.billPartyType === "decorator"
                      ? (poDecoratorName || supplierName)
                      : supplierName,
                poPartyName:
                  (billRow?.billPartyType === "shipping-account"
                    ? (poShippingAccountName || supplierName)
                    : billRow?.billPartyType === "decorator"
                      ? (poDecoratorName || supplierName)
                      : supplierName),
                _poId: po?.id,
                _poItems: poItems,
                _poSupplierInfo: poSupplierInfo,
                _poType: poType,
                billPartyType: isOtherServicePo ? "other-service" : billRow?.billPartyType,
              }));
              return;
            }

            if (!grouped[poNumber]) {
              grouped[poNumber] = [{
                id: null,
                poNumber,
                _poSource: "purchase-order",
                _poNumber: poNumber,
                _poStatus: po?.supplierStatus || "Created",
                _poPartyName: poShippingAccountName || supplierName,
                _poTargetAmount: resolvedPoAmount,
                poStatus: po?.supplierStatus || "Created",
                supplierInfo: supplierName,
                billSupplierInfo: { Name: supplierName },
                poPartyName: poShippingAccountName || supplierName,
                billPartyName: isOtherServicePo ? (poSupplierInfo.party.partyName || supplierName) : (poShippingAccountName || supplierName),
                billPartyType: isOtherServicePo ? "other-service" : undefined,
                poAmount: resolvedPoAmount,
                allBillsReceived: false,
                amount: null,
                createdAt: po?.createdAt || new Date().toISOString(),
                _poId: po?.id,
                _poItems: poItems,
                _poSupplierInfo: poSupplierInfo,
                _poType: poType,
              }];
            }
          });
        }
      } catch (e) {
        console.warn("[Billing] Unable to load Purchase Orders for bill gate:", e?.message || e);
      }

      setHasGeneratedPurchaseOrders(hasActiveGeneratedPurchaseOrders);
      setBillingGateLoaded(true);
      setGroupedBills(grouped);

      // Booked (Target): sum resolved PO targets, with legacy Billing.poAmount as fallback.
      let totalPOAmounts = 0;
      Object.entries(grouped).forEach(([po, billList]) => {
        const context = resolveBillingPoGroupContext(po, billList);
        if (context.hasPurchaseOrder && context.poTarget !== null && context.poTarget !== undefined) {
          totalPOAmounts += Number(context.poTarget) || 0;
        }
      });
      setFullBillAmount(totalPOAmounts);

      // Initialize "all bills received" flags
      const initialAllBillsStatus = {};
      Object.values(grouped).forEach((billList) => {
        billList.forEach((bill) => {
          initialAllBillsStatus[bill.id] = bill.allBillsReceived;
        });
      });
      setPoAllBillsReceivedStatus(initialAllBillsStatus);
    } catch (err) {
      console.error(err);
      setHasGeneratedPurchaseOrders(false);
      setOrderPurchaseOrders([]);
      setBillingGateLoaded(true);
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // --------------------------
  // Fetch SO stamp + invoices summary
  // --------------------------
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const so = await fetchLatestCompletedSalesOrderForOrder(orderId);
        if (alive) {
          setStampedBookedAmount(so?.stampedBookedAmount || null);
        }
      } catch (e) {
        console.warn("[Billing] fetchLatestCompletedSalesOrderForOrder:", e?.message);
      }
      try {
        const inv = await fetchInvoicesSummary(orderId);
        if (alive) {
          setInvoiceSummary({
            totalInvoiced: Number(inv?.totalInvoiced) || 0,
            paidInvoiced: Number(inv?.paidInvoiced) || 0,
            unpaidInvoiced: Number(inv?.unpaidInvoiced) || 0,
            count: Number(inv?.count) || 0,
            currency: inv?.currency || "CAD",
          });
        }
      } catch (e) {
        console.warn("[Billing] fetchInvoicesSummary:", e?.message);
      }
    })();
    return () => {
      alive = false;
    };
  }, [orderId]);

  // --------------------------
  // Derived totals from Bills
  // --------------------------
  const billedTotal = useMemo(() => {
    return Object.values(groupedBills || {}).reduce((sum, billList) => {
      if (!Array.isArray(billList) || billList.length === 0) return sum;

      const groupTotal = billList.reduce((s, b) => {
        return s + getBillSubtotal(b);
      }, 0);

      return sum + groupTotal;
    }, 0);
  }, [groupedBills]);

  // Per-PO computed map for the modal (target vs actual, variance, etc.)
  const poComputed = useMemo(() => {
    const map = {};
    Object.entries(groupedBills || {}).forEach(([po, bills]) => {
      if (!bills?.length) return;
      const context = resolveBillingPoGroupContext(po, bills);
      if (!context.hasPurchaseOrder) return;
      const target = Number(context.poTarget) || 0;
      const actual = bills.reduce((sum, bill) => sum + getBillSubtotal(bill), 0);
      map[po] = {
        target,
        actual,
        variance: actual - target,
        ownerBillId: context.source === "legacy-billing" ? bills.find((bill) => bill?.id)?.id : null,
        source: context.source,
        purchaseOrderId: context.purchaseOrderId,
        bills,
      };
    });
    return map;
  }, [groupedBills]);

  // --------------------------
  // Progress + Commission Gate
  // --------------------------
  const target = Number(fullBillAmount) || 0;     // PO target costs
  const actual = Number(billedTotal) || 0;        // pre-tax supplier billed actuals for PO reconciliation
  const invoiceSum = Math.max(0, Number(invoiceSummary.totalInvoiced) || 0); // client invoiced

  // --- Baseline display values (Costs use Booked Target = current PO targets) ---
  const baselineCosts = target; // Booked (Target): sum of resolved PO targets
  const baselineRevenue = Number(stampedBookedAmount?.targetRevenue) || 0;

  const baselineMargin =
    baselineRevenue > 0
      ? baselineRevenue - baselineCosts
      : Number(stampedBookedAmount?.bookedMargin) || 0;

  // single bar remains for Bills vs Target
  const rawPct =
    target > 0 ? (actual / target) * 100 : actual > 0 ? 200 : 0;
  const pctForTrack = Math.min(100, Math.max(0, rawPct)); // 0..100 for main bar
  const overPercent = Math.max(0, Math.min(100, rawPct - 100)); // layer for 100..200%

  const remaining = Math.max(0, target - actual);
  const overage = Math.max(0, actual - target);
  const overPct = target > 0 ? (overage / target) * 100 : actual > 0 ? 100 : 0;

  // Commission eligibility gate: only pay up to what's BOTH billed & invoiced
  const commissionEligible = Math.max(0, Math.min(actual, invoiceSum));

  // Prefer prop, else stamped booked margin
  const bookedMarginForUI =
    Number(bookedMargin) ||
    Number(stampedBookedAmount?.bookedMargin) ||
    0;

  // Push summary up to parent (debounced via ref)
  const onComputedRef = useRef(onComputed);
  useEffect(() => {
    onComputedRef.current = onComputed;
  }, [onComputed]);

  const computedPayload = useMemo(
    () => ({
      // Bills context (for progress)
      target,
      actual,
      delta: target - actual,
      remaining,
      overage,
      rawPct, // % of target (can exceed 100)

      // Sales Order stamped context
      stamped: stampedBookedAmount || null, // { targetRevenue, bookedMargin, by, at }

      // Invoices context
      invoicedTotal: invoiceSum, // total invoiced to client
      commissionEligible, // min(billed, invoiced)

      // Margin display for sibling tabs
      bookedMargin: bookedMarginForUI,
    }),
    [
      target,
      actual,
      remaining,
      overage,
      rawPct,
      stampedBookedAmount,
      invoiceSum,
      commissionEligible,
      bookedMarginForUI,
    ]
  );

  useEffect(() => {
    if (typeof onComputedRef.current === "function") {
      onComputedRef.current(computedPayload);
    }
  }, [computedPayload]);

  // Friendly status badge/message (for Bills vs Target)
  const getProgressBadge = () => {
    const p = Math.max(0, rawPct);
    if (target <= 0 && actual > 0) {
      return {
        color: "warning",
        label: "No target set",
        sub: `Billed ${currency(actual)}`,
      };
    }
    if (p >= 150) {
      return {
        color: "error",
        label: "Over target (150%+)",
        sub: `Over by ${currency(overage)} · ${p.toFixed(0)}%`,
      };
    }
    if (p > 100) {
      return {
        color: "warning",
        label: "Over target",
        sub: `Over by ${currency(overage)} · ${p.toFixed(0)}%`,
      };
    }
    if (p === 100) {
      return {
        color: "success",
        label: "Billed/booked",
        sub: "100% match",
      };
    }
    if (p >= 50) {
      return {
        color: "info",
        label: "Halfway there",
        sub: `${p.toFixed(0)}% complete · Remaining ${currency(remaining)}`,
      };
    }
    if (p > 0) {
      return {
        color: "default",
        label: "Getting started",
        sub: `${p.toFixed(0)}% · Remaining ${currency(remaining)}`,
      };
    }
    return {
      color: "default",
      label: "Not started",
      sub: `Target ${currency(target)}`,
    };
  };
  const badge = getProgressBadge();

  // Color logic for the “context” (used in legend text)
  const getContextColors = () => {
    if (target <= 0 && actual > 0) {
      return { bar: "#d32f2f", track: "#ffebee" };
    }
    if (actual > target) {
      return { bar: "#d32f2f", track: "#ffebee" };
    }
    const ratio = actual / (target || 1);
    if (ratio >= 0.9) {
      return { bar: "#ed6c02", track: "#fff3e0" };
    }
    return { bar: "#2e7d32", track: "#e8f5e9" };
  };
  const { bar } = getContextColors();
  const billsAvailable = hasGeneratedPurchaseOrders || Object.values(groupedBills || {})
    .some((billList) => (billList || []).some((bill) => bill?.id));

  if (loading || !billingGateLoaded) {
    return (
      <Box sx={{ width: "100%", justifyContent: "center" }}>
        <LoadingComponent />
      </Box>
    );
  }

  if (!billsAvailable) {
    return (
      <Paper
        elevation={0}
        sx={{
          border: "1px solid #e5e7eb",
          borderRadius: 2,
          p: 4,
          textAlign: "center",
          backgroundColor: "#fafafa",
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
          Bills will appear once Purchase Orders have been generated for this Sales Order.
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Create at least one Purchase Order to start tracking supplier bills.
        </Typography>
      </Paper>
    );
  }

  const invoiceHint =
    invoiceSum < actual
      ? "Billed costs exceed client invoices — consider invoicing the difference."
      : invoiceSum > actual
      ? "Client invoices exceed supplier bills — ensure all supplier bills are entered."
      : "";

  // Tooltip bodies
  const ttBookedTarget = (
    <Box sx={{ p: 0.5 }}>
      <b>Booked (Target)</b> = sum of actual Purchase Order targets when available.
      <br />
      Legacy bill target snapshots are used only when a PO cannot be resolved.
    </Box>
  );

  const ttBilledActual = (
    <Box sx={{ p: 0.5 }}>
      <b>Billed (Actual)</b> = sum of supplier bills entered to date.
      <ul style={{ margin: 0, paddingLeft: "1.25rem" }}>
        <li>
          Uses <code>bill.subTotal</code> when present, otherwise included bill-item pre-tax subtotals.
        </li>
        <li>Includes shipping/misc if entered on the bill.</li>
        <li>Reflects what Finance actually entered (to-date).</li>
      </ul>
    </Box>
  );

  const ttInvoicedClient = (
    <Box sx={{ p: 0.5 }}>
      <b>Invoiced (Client)</b> = sum of client-facing invoices for this order
      (paid + unpaid, excluding void/cancelled).
      <br />
      Used to ensure commissions are only paid against revenue that was
      actually invoiced.
    </Box>
  );

  const ttCommissionable = (
    <Box sx={{ p: 0.5 }}>
      <b>Commissionable (to-date)</b> ={" "}
      <b>min(Billed&nbsp;Actual, Invoiced&nbsp;Client)</b>.
      <br />
      This caps payout by both sides: supplier bills and client invoices.
    </Box>
  );

  const ttSOStamp = (
    <Box sx={{ p: 0.5 }}>
      <b>SO (Signed) Baseline</b> is captured when the Sales Order is approved (status “3”).
      For this view, <b>Costs</b> shows <b>Booked (Target)</b> (sum of PO targets currently entered)
      so Finance always sees the live PO plan alongside the signed revenue.
    </Box>
  );

  const ttCostsUseBookedTarget = (
    <Box sx={{ p: 0.5 }}>
      <b>Costs</b> = <b>Booked (Target)</b> — the sum of resolved PO targets.
      This is live (based on current POs), not the frozen SO snapshot.
    </Box>
  );

  const ttAllBillsReceived = (
    <Box sx={{ p: 0.5 }}>
      <b>All Bills Received</b> (per row) is an operational flag to mark that
      every vendor’s bill for that PO line has been received/entered.
      <br />
      Use it to track completion by PO.
    </Box>
  );

  const ttProgressBar = (
    <Box sx={{ p: 0.5 }}>
      Main bar shows % of <b>Billed (Actual)</b> vs <b>Booked (Target)</b>.
      <br />
      Red striped overlay visualizes 100–200% when bills exceed target.
    </Box>
  );

  const ttPOGrouping = (
    <Box sx={{ p: 0.5 }}>
      Bills are grouped by <b>PO Number</b>. PO targets come from Purchase
      Orders when available; legacy bill target snapshots are fallback only.
    </Box>
  );

  return (
    <>
      {/* <div className="order-header-action">
        <h2>
          Bills
          <InfoDot title={ttPOGrouping} />
        </h2>
        <div className="dropdown-container">
          <Box
            className="introduction-field"
            sx={{ display: "flex", justifyContent: "center" }}
          >
            <Button
              variant="contained"
              onClick={toggleBillActions}
              sx={{
                textTransform: "none",
                color: "white",
                backgroundColor: "#c3171d",
                "&:hover": { backgroundColor: "white", color: "#c3171d" },
                width: "200px",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
                gap: "0.5rem",
              }}
            >
              {loading ? (
                <ButtonLoader />
              ) : (
                <>
                  <SettingsIcon /> Actions
                </>
              )}
            </Button>
          </Box>

          {showBillActions && (
            <>
              <Box
                className="dropdown-menu"
                sx={{
                  position: "absolute",
                  mt: 1,
                  right: 0,
                  background: "#fff",
                  border: "1px solid #eee",
                  borderRadius: 1,
                  p: 1,
                  boxShadow: 2,
                  zIndex: 10,
                  minWidth: 240,
                }}
              >
                <Tooltip title="" arrow>
                  <span>
                    <Button
                      onClick={() => {
                        openNewBill();
                        setShowBillActions(false);
                      }}
                      sx={{
                        width: "100%",
                        marginBottom: "10px",
                        fontWeight: 700,
                        textTransform: "none",
                      }}
                    >
                      Add Bill
                    </Button>
                  
                  </span>
                </Tooltip>
              </Box>
            </>
          )}
        </div>
      </div> */}

        {/* PO-driven bill creation is now inline in the table via "Add Bill" per PO row. */}

      {(() => {
        const exceeds = target > 0 && actual > target;
        const exactComplete = target > 0 && Number(actual.toFixed(2)) === Number(target.toFixed(2));
        const delta = Math.max(0, actual - target);
        const message = exceeds
          ? `🔴 You currently have confirmed ${currency(actual)} / ${currency(target)} from purchase orders. The amount has exceeded ${currency(delta)}.`
          : exactComplete
            ? "✅ Complete amount has been paid for all purchase orders."
            : `🟠 You currently have confirmed ${currency(actual)} / ${currency(target)} from purchase orders.`;
        const palette = exceeds
          ? { bg: "#fdecea", border: "#f5c2c7", text: "#842029" }
          : exactComplete
            ? { bg: "#d4edda", border: "#c3e6cb", text: "#155724" }
            : { bg: "#fff3cd", border: "#ffeeba", text: "#856404" };

        return (
          <Box
            className="status-message"
            sx={{
              backgroundColor: palette.bg,
              p: 2,
              borderRadius: 2,
              mb: 2,
              border: `1px solid ${palette.border}`,
            }}
          >
            <Typography sx={{ color: palette.text }}>
              {message}
            </Typography>
          </Box>
        );
      })()}

      <BillingList
        onBillClick={onBillClick}
        setLoading={setLoading}
        fetchData={fetchData}
        setPoAllBillsReceivedStatus={setPoAllBillsReceivedStatus}
        loading={loading}
        groupedBills={groupedBills}
        getPurchaseOrderHref={getPurchaseOrderHref}
        poAllBillsReceivedStatus={poAllBillsReceivedStatus}
        fullBillAmount={fullBillAmount}
        onAddBill={openNewBill}
        onAddBillFromPo={openBillFromPo}
        financialSummary={financialSummary}
        currencyCode={financialSummary.currency || invoiceSummary.currency}
      />

      {billingModalopen && (
        <BillingModal
          fetchData={fetchData}
          poData={poNumber}
          poAmountData={poAmount}
          poStatusData={poStatus}
          poSeedData={poSeedData}
          billOrderId={orderId}
          billId={billId}
          onClose={closeBillingModal}
          open={billingModalopen}
          orderId={orderId}
          purchaseOrderOptions={orderPurchaseOrders}
          // Right-panel context + override hook
          poGroupBills={(poNumber && groupedBills?.[poNumber]) || []}
          poComputedForPo={poNumber ? poComputed?.[poNumber] : null}
          onOverridePoAmount={async (newAmount) => {
            const ownerId = poComputed?.[poNumber]?.ownerBillId;
            if (!ownerId) return;
            await updateBill(ownerId, { poAmount: Number(newAmount) || 0 });
            await fetchData();
          }}
        />
      )}
    </>
  );
};

export default Billing;
